import {useEffect, useRef, useState} from "react";
import {useAbandonedBookingFlowStateQuery} from "./useBookingProgressQuery";
import {useMonolithLocationFieldsBySlugQuery} from "@services/graphql/queries/location";
import {useFetchAppointmentReasonsQuery} from "@page-containers/booking/graphql/__generated__/FetchAppointmentReasons.type";
import {compact} from "lodash";
import {loadAvailableSlots} from "@services/api";
import {buildBookingLink, useParsedBookingQuery} from "../bookingQuery";
import {useRouter} from "next/router";
import {setEmail} from "../emailStorage";
import {v4} from "uuid";
import {queueEvent} from "src/utils/analytics/eventQueue";
import {EventType} from "src/utils/analytics/constants";

/**
 * This hook:
 * 1. Attempts to find an abandoned booking flow
 * 2. If found, it loads the location, appointment reason, and checks the selected slot time
 * 3. Sets the relevant parameters into
 */
export const useContinueAbandonedFlow = () => {
  const hasFlowBeenRestarted = useRef(false);
  const [slotsLoading, setSlotsLoading] = useState(true);
  const [flowId] = useState(v4());
  const router = useRouter();
  const query = useParsedBookingQuery();
  const [dropoutVisitorId] = useState(query.dropoutVisitorId);
  const abandonedFlowStateQuery = useAbandonedBookingFlowStateQuery({
    variables: {
      visitorId: dropoutVisitorId || "",
    },
    skip: !dropoutVisitorId,
  });
  const schedulingFlowState = abandonedFlowStateQuery.data?.schedulingFlowState;

  const locationQuery = useMonolithLocationFieldsBySlugQuery(
    schedulingFlowState?.locationSlug || null,
  );
  const location = locationQuery.data?.location;

  const apptReasonQuery = useFetchAppointmentReasonsQuery({
    variables: {
      slugs: compact([schedulingFlowState?.apptReasonSlug]),
    },
    skip: !schedulingFlowState?.apptReasonSlug,
  });
  const appointmentReason = apptReasonQuery.data?.practice?.appointmentReasons?.nodes?.[0];

  useEffect(() => {
    if (!dropoutVisitorId) {
      return setSlotsLoading(false);
    }
    if (!schedulingFlowState) return;
    if (!schedulingFlowState.slotTime) return;
    if (!location) return;
    if (!appointmentReason) return;
    if (!slotsLoading) return;

    // This is a safeguard to prevent the flow from being restarted multiple times i.e. on route change
    if (hasFlowBeenRestarted.current) return;
    hasFlowBeenRestarted.current = true;

    queueEvent({
      typeId: EventType.RestartingAbandonedFlow,
      extraData: {
        flowId,
        dropoutVisitorId: dropoutVisitorId,
        abandonedFlowId: schedulingFlowState.flowId,
        isBooked: schedulingFlowState.isBooked,
      },
    });

    if (schedulingFlowState.isBooked) {
      router
        .replace(
          buildBookingLink({
            region: schedulingFlowState.region,
            locationSlug: location.slug,
            specialtyId: schedulingFlowState.specialtyId,
            appointmentReasonSlug: appointmentReason.slug,
            flowId,
          }),
        )
        .finally(() => {
          setSlotsLoading(false);
        });
    } else {
      setEmail(schedulingFlowState.patientInfo?.email || "");

      loadAvailableSlots({
        practiceId: location.practiceId,
        locationId: location.id,
        from: schedulingFlowState.slotTime,
        to: schedulingFlowState.slotTime + 1000,
        limit: 1,
        doctorId: schedulingFlowState.doctorId,
        timezone: location.timezone,
        virtual: false,
        reasonId: appointmentReason.id,
        specialtyId: schedulingFlowState.specialtyId,
        enableCache: false,
      })
        .then(slots => {
          const slot = slots?.[0]?.[0];

          return router.replace(
            buildBookingLink({
              region: schedulingFlowState.region,
              selectedPatientId: schedulingFlowState.patientInfo?.selectedPatientId,
              locationSlug: location.slug,
              selectedTime: slot?.time,
              specialtyId: schedulingFlowState.specialtyId,
              doctorId: slot?.doctorId,
              appointmentReasonSlug: appointmentReason.slug,
              selectedSlotIds: slot?.slotIds,
              flowId,
            }),
          );
        })
        .finally(() => {
          setSlotsLoading(false);
        });
    }
  }, [
    appointmentReason,
    dropoutVisitorId,
    location,
    router,
    schedulingFlowState,
    slotsLoading,
    flowId,
  ]);

  return {
    schedulingFlowState,
    dropoutVisitorId,
    loading:
      slotsLoading ||
      abandonedFlowStateQuery.loading ||
      locationQuery.loading ||
      apptReasonQuery.loading,
  };
};
